import { useCallback, useEffect, useMemo, useState } from 'react'

import type { CatalogItemDto } from '../api/catalogTypes'
import { displaySupplierName } from '../utils/supplierName'
import {
  listRestaurantMyPricesItems,
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/myPrices'
import { addToCart, setCartQuantity } from '../cart/cartStore'
import { useCart } from '../hooks/useCart'
import { formatUnitLabel } from '../utils/formatUnitLabel'

type SearchScreenProps = {
  organizationId: number
}

const formatPrice = (price: number | null): string => {
  if (price == null) {
    return '—'
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(price)
}

const formatDelta = (pct: number | null): string | null => {
  if (pct == null || !Number.isFinite(pct)) {
    return null
  }
  const rounded = Math.round(pct * 10) / 10
  if (rounded > 0) {
    return `▲ ${rounded}%`
  }
  if (rounded < 0) {
    return `▼ ${Math.abs(rounded)}%`
  }
  return null
}

const rawItemName = (item: CatalogItemDto): string => {
  const positionName = item.positionName?.trim() ?? ''
  if (positionName) {
    return positionName
  }
  const supplierName = item.supplierName?.trim() ?? ''
  const country = item.country?.trim() ?? ''
  if (supplierName && supplierName.toLowerCase() !== country.toLowerCase()) {
    return supplierName
  }
  if (item.productTypeName?.trim()) {
    return item.productTypeName.trim()
  }
  if (supplierName) {
    return supplierName
  }
  if (item.externalSku?.trim()) {
    return item.externalSku.trim()
  }
  return `Позиция #${item.id}`
}

const truncateForCard = (value: string, maxChars = 46): string => {
  if (value.length <= maxChars) {
    return value
  }
  return `${value.slice(0, Math.max(0, maxChars - 3)).trimEnd()}...`
}

const displayItemName = (item: CatalogItemDto): string =>
  truncateForCard(rawItemName(item))

const cardMeta = (item: CatalogItemDto): string => {
  // Ед.изм. опускаем — единица уже видна в ценнике («… / кг»).
  const country = item.country?.trim() || '—'
  const brand = item.brand?.trim() || '—'
  return `Страна: ${country} · Бренд: ${brand}`
}

export const SearchScreen = ({ organizationId }: SearchScreenProps) => {
  const { lines: cartLines } = useCart()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [supplierId, setSupplierId] = useState<number | null>(null)
  const [priceSort, setPriceSort] = useState<'asc' | 'desc' | null>(null)
  const [items, setItems] = useState<CatalogItemDto[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [suppliers, setSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [page, supplierList] = await Promise.all([
        listRestaurantMyPricesItems({
          organizationId,
          search: debounced || undefined,
          privateSupplierId: supplierId,
          size: 100,
        }),
        listRestaurantPrivateSuppliers(organizationId),
      ])
      setItems(page.items)
      setTotalElements(page.totalElements)
      setSuppliers(supplierList)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить прайсы')
    } finally {
      setLoading(false)
    }
  }, [debounced, organizationId, supplierId])

  useEffect(() => {
    void load()
  }, [load])

  const sortedItems = useMemo(() => {
    if (!priceSort) {
      return items
    }
    return [...items].sort((a, b) => {
      const pa = a.price ?? Number.POSITIVE_INFINITY
      const pb = b.price ?? Number.POSITIVE_INFINITY
      return priceSort === 'asc' ? pa - pb : pb - pa
    })
  }, [items, priceSort])

  const qtyOf = (catalogItemId: number) =>
    cartLines.find((line) => line.catalogItemId === catalogItemId)?.quantity ?? 0

  const handleAdd = (item: CatalogItemDto) => {
    if (item.privateSupplierId == null) {
      return
    }
    addToCart({
      catalogItemId: item.id,
      name: rawItemName(item),
      privateSupplierId: item.privateSupplierId,
      privateSupplierName: item.privateSupplierName ?? `Поставщик #${item.privateSupplierId}`,
      unit: formatUnitLabel(item.unit),
      unitPrice: item.price,
      note: null,
      brand: item.brand,
    })
  }

  const handleQty = (item: CatalogItemDto, next: number) => {
    if (next <= 0) {
      setCartQuantity(item.id, 0)
      return
    }
    if (qtyOf(item.id) === 0) {
      handleAdd(item)
      if (next > 1) {
        setCartQuantity(item.id, next)
      }
      return
    }
    setCartQuantity(item.id, next)
  }

  return (
    <section className="rp-screen" aria-label="Поиск по прайсам">
      <label className="rp-search">
        <span className="rp-search__icon" aria-hidden="true">
          ⌕
        </span>
        <span className="visually-hidden">Поиск</span>
        <input
          className="rp-search__input"
          type="search"
          placeholder="Найти позицию…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>

      <div className="rp-chips" role="group" aria-label="Поставщики">
        <button
          type="button"
          className={supplierId == null ? 'echat-chip echat-chip--selected' : 'echat-chip'}
          onClick={() => setSupplierId(null)}
        >
          Все прайсы
        </button>
        {suppliers.map((supplier) => (
          <button
            key={supplier.id}
            type="button"
            className={
              supplierId === supplier.id ? 'echat-chip echat-chip--selected' : 'echat-chip'
            }
            onClick={() => setSupplierId(supplier.id)}
          >
            {displaySupplierName(supplier.displayLabel, supplier.legalName)}
          </button>
        ))}
      </div>

      <div className="rp-toolbar rp-toolbar--split">
        <span className="rp-toolbar__count">
          {!loading && totalElements > 0
            ? `${totalElements.toLocaleString('ru-RU')} позиц. найдено`
            : ''}
        </span>
        <button
          type="button"
          className="echat-btn echat-btn--ghost rp-sort"
          onClick={() =>
            setPriceSort((current) =>
              current === null ? 'asc' : current === 'asc' ? 'desc' : null,
            )
          }
        >
          {priceSort === 'asc'
            ? 'По цене ↑'
            : priceSort === 'desc'
              ? 'По цене ↓'
              : 'По цене'}
        </button>
      </div>

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="echat-muted">Загрузка…</p> : null}

      {!loading && sortedItems.length === 0 ? (
        <div className="echat-inbox__empty">
          <p className="echat-inbox__empty-title">Ничего не найдено</p>
          <p className="echat-muted">
            Загрузите прайс на вкладке «Прайсы» или измените запрос.
          </p>
        </div>
      ) : null}

      <ul className="rp-list">
        {sortedItems.map((item) => {
          const qty = qtyOf(item.id)
          const delta = formatDelta(item.priceDeltaPct)
          return (
            <li key={item.id} className="rp-card">
              <div className="rp-card__main">
                <p className="rp-card__title">{displayItemName(item)}</p>
                <p className="rp-card__meta">{cardMeta(item)}</p>
                <div className="rp-card__pricerow">
                  <p className="rp-card__price">
                    {formatPrice(item.price)}
                    <span className="rp-card__unit"> / {formatUnitLabel(item.unit)}</span>
                  </p>
                  {delta ? (
                    <span
                      className={
                        (item.priceDeltaPct ?? 0) > 0
                          ? 'rp-delta rp-delta--up'
                          : 'rp-delta rp-delta--down'
                      }
                    >
                      {delta}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="rp-card__side">
                {item.productTypeName ? (
                  <p className="rp-card__cat">{item.productTypeName}</p>
                ) : null}
                {qty === 0 ? (
                  <button
                    type="button"
                    className="echat-btn echat-btn--primary rp-card__add"
                    disabled={item.privateSupplierId == null}
                    onClick={() => handleAdd(item)}
                    aria-label="В корзину"
                  >
                    +
                  </button>
                ) : (
                  <div className="rp-stepper" role="group" aria-label="Количество">
                    <button
                      type="button"
                      className="rp-stepper__btn"
                      aria-label="Уменьшить"
                      onClick={() => handleQty(item, qty - 1)}
                    >
                      −
                    </button>
                    <span className="rp-stepper__qty">{qty}</span>
                    <button
                      type="button"
                      className="rp-stepper__btn"
                      aria-label="Увеличить"
                      onClick={() => handleQty(item, qty + 1)}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
